import {
  CONTENT_MEDIA_POSITIONS,
  type ContentMediaPosition,
  type HeroMediaKind,
  LANDING_PAGE_CTA_MODES,
  LANDING_PAGE_LOGO_SIZES,
  type LandingPageBlockType,
  type LandingPageCtaMode,
  type LandingPageLogoSize,
} from '@provision/landing-page-utils';

export type { LandingPageBlockType };

// Every field carries a value whatever the row's `type`: the form's key set must not change, or
// the whole-form dirty check reads a fresh row as dirty. Unowned fields are dropped on serialize.
export interface LandingPageBlockValues {
  id: string;
  type: LandingPageBlockType;

  heroHeadline: string;
  heroSubtext: string;
  heroMediaKind: HeroMediaKind;
  heroVideoUrl: string;
  heroVideoTitle: string;
  heroVideoCaption: string;
  heroImageUrl: string;

  contentKicker: string;
  contentTitle: string;
  contentBody: string;
  contentImageUrl: string;
  contentMediaPosition: ContentMediaPosition;

  ctaHeadline: string;
  ctaSubtext: string;
  ctaButtonLabel: string;
}

export interface LandingPageSettingsValues {
  ctaMode: LandingPageCtaMode;
  consultationUrl: string;
  showPricing: boolean;
  logoSize: LandingPageLogoSize;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
}

export interface LandingPageFormValues {
  name: string;
  slug: string;
  blocks: LandingPageBlockValues[];
  settings: LandingPageSettingsValues;
}

const CTA_MODE_LABELS: Record<LandingPageCtaMode, string> = {
  consultation: 'Consultation',
  signup: 'Sign up',
};

const LOGO_SIZE_LABELS: Record<LandingPageLogoSize, string> = {
  standard: 'Standard',
  large: 'Large',
};

const MEDIA_POSITION_LABELS: Record<ContentMediaPosition, string> = {
  auto: 'Auto',
  left: 'Left',
  right: 'Right',
};

export const CTA_MODE_OPTIONS = LANDING_PAGE_CTA_MODES.map((value) => ({
  value,
  label: CTA_MODE_LABELS[value],
}));

export const LOGO_SIZE_OPTIONS = LANDING_PAGE_LOGO_SIZES.map((value) => ({
  value,
  label: LOGO_SIZE_LABELS[value],
}));

export const MEDIA_POSITION_OPTIONS = CONTENT_MEDIA_POSITIONS.map((value) => ({
  value,
  label: MEDIA_POSITION_LABELS[value],
}));

export const BLOCK_TYPE_LABELS: Record<LandingPageBlockType, string> = {
  hero: 'Hero',
  content: 'Content section',
  plans: 'Membership plans',
  faq: 'FAQ',
  closingCta: 'Call to Action',
};

export const BLOCK_TYPE_ICONS: Record<LandingPageBlockType, string> = {
  hero: 'image',
  content: 'view_agenda',
  plans: 'list_alt',
  faq: 'help',
  closingCta: 'campaign',
};

export const BLOCK_SYNCED_NOTES: Partial<Record<LandingPageBlockType, string>> = {
  plans:
    'Plans, pricing, features, and tooltips render from your membership configuration and stay in sync automatically. Edit in Membership Options.',
  faq: 'Questions and answers sync from your activation flow configuration.',
};

export const BLOCK_TYPE_ORDER = Object.keys(BLOCK_TYPE_LABELS) as LandingPageBlockType[];

type LandingPageBlockField = Exclude<keyof LandingPageBlockValues, 'id' | 'type'>;

export function blockFieldName<F extends LandingPageBlockField>(
  index: number,
  field: F,
): `blocks.${number}.${F}` {
  return `blocks.${index}.${field}`;
}
